import { useTranslation } from 'react-i18next';
import { cn } from '@resource-kit/ui';
import type { BoardTarget } from './boardModel';
import { FOCUS_BADGE_LABEL, type FocusCard, type FocusText, type FocusTone } from './focusCards';

// File name: not `FocusCards.tsx`, because on a case-insensitive file system
// the import `./FocusCards` would resolve to the rule module `focusCards.ts`.

// Badge colours per tone: the status colour draws the edge and a /10 tint, the
// text stays in the body colour (ADR-0043 decision 3). `live` is the progress
// teal (`Dispatch.dc.html:220,228`).
const TONE_CLASS = {
  live: 'border border-status-assigned bg-status-assigned/10',
  block: 'border border-destructive bg-destructive/10',
  ok: 'border border-success bg-success/10',
  attn: 'border border-warning bg-warning/10',
  mut: 'border border-border bg-muted',
} as const satisfies Record<FocusTone, string>;

// The gate card is the one emphasis on the screen (ADR-0043 §5): its badge takes
// the primary tint, and the card takes a 2px inset ring plus the hard shadow.
// Judged by badge, not tone: the progress card is `live` too.
const GATE_BADGE_CLASS = 'border border-primary bg-primary/10';

interface FocusCardsProps {
  cards: FocusCard[];
  /** No card, every read answered, and today's counts show nothing to act on. */
  allClear: boolean;
  /** A read the cards need failed: say so rather than leave the area blank. */
  failed: boolean;
  onSelect: (target: BoardTarget) => void;
}

export function FocusCards({ cards, allClear, failed, onSelect }: FocusCardsProps) {
  const { t } = useTranslation();
  const say = (copy: FocusText) => t(copy.key, copy.params);

  return (
    <section aria-label={t('dispatch.board.focus.title')} data-testid="dispatch-focus-cards">
      {cards.length === 0 ? (
        failed ? (
          <p className="text-sm text-destructive">{t('error.loadFailed')}</p>
        ) : allClear ? (
          <p className="text-sm text-muted-foreground">{t('dispatch.board.focus.allClear')}</p>
        ) : null
      ) : (
        <div
          data-testid="dispatch-focus-card-grid"
          className="grid gap-2.5 pr-1.5 pb-1.5"
          // One column per card (1-4), so a short row has no empty cells.
          style={{ gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))` }}
        >
          {cards.map((card) => {
            const isGate = card.badge === FOCUS_BADGE_LABEL.gate;
            return (
              <button
                key={card.kind}
                type="button"
                data-testid={`dispatch-focus-card-${card.kind}`}
                onClick={() => onSelect(card.target)}
                className={cn(
                  'flex flex-col items-start gap-1.5 rounded-xl bg-card px-3.5 py-3 text-left ring-1 ring-inset ring-border/60 transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  // The gate card already wears the focus ring, so keyboard focus adds
                  // an offset outline to stay visible (WCAG 2.4.7).
                  isGate &&
                    'ring-2 ring-ring shadow-hard focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                )}
              >
                <span
                  data-testid="dispatch-focus-card-badge"
                  className={cn(
                    'rounded-sm px-2 text-xs font-medium leading-[20px] whitespace-nowrap text-foreground',
                    isGate ? GATE_BADGE_CLASS : TONE_CLASS[card.tone],
                  )}
                >
                  {t(card.badge)}
                </span>
                <span
                  data-testid="dispatch-focus-card-title"
                  className="text-sm font-semibold leading-[1.35]"
                >
                  {say(card.title)}
                </span>
                {card.sub ? (
                  <span
                    data-testid="dispatch-focus-card-sub"
                    className="text-[12.5px] text-muted-foreground"
                  >
                    {say(card.sub)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
